import { createContext, useContext, useEffect, useReducer, type ReactElement, type ReactNode } from 'react';
import { container } from 'tsyringe';
import { ThemeService, type ThemeMode, type ThemeStyle } from '@/shared/ThemeService';

export type { ThemeMode, ThemeStyle };

interface ThemeContextValue {
    mode: ThemeMode;
    style: ThemeStyle;
    setMode: (mode: ThemeMode) => void;
    setStyle: (style: ThemeStyle) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Adapter that bridges ThemeService (DI singleton) with React context.
 * Subscribes to ThemeService changes and re-renders consuming components.
 */
export function ThemeContextProvider({ children }: { children: ReactNode }): ReactElement {
    const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
    const themeService = container.resolve(ThemeService);

    useEffect(() => {
        return themeService.subscribe(() => forceUpdate());
    }, [themeService]);

    const value: ThemeContextValue = {
        mode: themeService.mode,
        style: themeService.style,
        setMode: (mode) => themeService.setMode(mode),
        setStyle: (style) => themeService.setStyle(style),
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeContext(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useThemeContext must be used within ThemeContextProvider');
    return ctx;
}
